import { fetchRequestSend, setCookie } from "../../helpers/common-helpers";
import AssessmentCaptivatePublic from "./publicAssessments/assessmentCaptivatePublic";
import AssessmentMultipleChoicePublic from "./publicAssessments/AssessmentMultipleChoicePublic";
import AssessmentMultipleResponsePublic from "./publicAssessments/assessmentMultipleResponsePublic";
import AssessmentMatchingPublic from "./publicAssessments/assessmentMatchingPublic";
import AssessmentSequencePublic from "./publicAssessments/assessmentSequencePublic";
import PopupClass from "../../components/common/PopupClass";
import { CGSkillsStatusUpdate } from "../../components/common/CoursePassingProcessClass";
import l10n from "../../lang/locallang";
import {
    handleDragAndDropForNewAssessments,
    setItemsHeightForNewAssessment
} from "../../assessments/CreateAssessmentHelpers";

/**
 * Public-facing assessment runner (pre/post) that mirrors LMS behavior but stores progress in cookies.
 */
export default class AssessmentsWebsiteProcess {
    constructor() {
        this.topicId = null;
        this.assessmentType = null;
        this.parentAssessmentId = null;
        this.stage = null; // pre|post
    }

    /**
     * Entrypoint for a public assessment.
     */
    run({ topicId, assessmentType, parentAssessId = null, stage = "pre" }) {
        this.topicId = topicId;
        this.assessmentType = String(assessmentType);
        this.parentAssessmentId = parentAssessId;
        this.stage = stage;

        const url = new URL(window.location.href);
        url.searchParams.set("topic_id", this.topicId);
        url.searchParams.set("stage", this.stage);
        window.history.replaceState({}, "", url);

        this.loadAssessmentContent();
    }

    async loadAssessmentContent() {
        try {
            if (typeof getTopicContentURL === "undefined") {
                throw new Error("Missing getTopicContentURL");
            }
            const jsonData = {
                topic_id: this.topicId,
                parent_assess_id: this.parentAssessmentId
            };
            const response = await fetchRequestSend(getTopicContentURL, "POST", jsonData);
            this.initializeAssessmentView(response);
        } catch (error) {
            console.error("Error loading assessment content:", error);
        }
    }

    initializeAssessmentView(response) {
        let assessment;
        let assessmentPopupHtml;
        const popup = new PopupClass();
        const popupElement = document.getElementById("take-assessment-popup");

        if (!popupElement) {
            return;
        }

        popupElement.dataset.topicId = this.topicId;
        popupElement.dataset.assessmentType = this.stage;
        popupElement.dataset.assessmentId = this.assessmentType;

        const questionEl = this.getFirstAvailable(popupElement, [".cg-popup__question", ".popup-title"]);
        const bodyEl = this.getFirstAvailable(popupElement, [".cg-popup__body", ".popup-body"]);
        const resultEl = this.getFirstAvailable(popupElement, [".cg-popup__result", ".assessment-result"]);
        const assessTypeEl = this.getFirstAvailable(popupElement, [".cg-popup__assess-type", ".assessment-popup-header__title"]);
        const assessHintEl = this.getFirstAvailable(popupElement, [".cg-popup__assess-hint", ".__select-all"]);
        const actionsEl = this.getFirstAvailable(popupElement, [".cg-popup__actions", ".buttons-set"]);

        switch (this.assessmentType) {
            case "1":
            case "2":
                assessment = new AssessmentCaptivatePublic().view(
                    response,
                    this.assessmentType === "1" ? "small" : "large",
                    this.topicId,
                    this.parentAssessmentId,
                    this.stage
                );
                popupElement.classList.remove("is-incorrect", "is-correct");
                if (resultEl) resultEl.innerHTML = "";
                break;
            case "3":
                assessmentPopupHtml = new AssessmentMultipleChoicePublic().view(response);
                popup.open("take-assessment-popup");
                if (questionEl) questionEl.innerHTML = response.question;
                if (bodyEl) bodyEl.innerHTML = assessmentPopupHtml;
                popupElement.classList.remove("is-incorrect", "is-correct");
                if (resultEl) resultEl.innerHTML = "";
                if (assessTypeEl) assessTypeEl.innerHTML = l10n.assessmentMultipleChoice.header;
                if (assessHintEl) assessHintEl.innerHTML = l10n.assessmentMultipleChoice.hint;
                this.buttonsAdding(actionsEl, this.stage);
                this.setHeaderText(popupElement, this.stage);
                break;
            case "4":
                assessmentPopupHtml = new AssessmentMultipleResponsePublic().view(response);
                popup.open("take-assessment-popup");
                if (questionEl) questionEl.innerHTML = response.question;
                if (bodyEl) bodyEl.innerHTML = assessmentPopupHtml;
                this.buttonsAdding(actionsEl, this.stage);
                popupElement.classList.remove("is-incorrect", "is-correct");
                if (resultEl) resultEl.innerHTML = "";
                if (assessTypeEl) assessTypeEl.innerHTML = l10n.assessmentMultipleResponse.header;
                if (assessHintEl) assessHintEl.innerHTML = l10n.assessmentMultipleResponse.hint;
                this.setHeaderText(popupElement, this.stage);
                break;
            case "5":
                assessmentPopupHtml = new AssessmentMatchingPublic().view(response);
                popup.open("take-assessment-popup");
                if (questionEl) questionEl.innerHTML = response.question;
                if (bodyEl) bodyEl.innerHTML = assessmentPopupHtml;
                this.buttonsAdding(actionsEl, this.stage);
                setItemsHeightForNewAssessment(popupElement);
                handleDragAndDropForNewAssessments(popupElement);
                popupElement.classList.remove("is-incorrect", "is-correct");
                if (resultEl) resultEl.innerHTML = "";
                if (assessTypeEl) assessTypeEl.innerHTML = l10n.assessmentMatching.header;
                if (assessHintEl) assessHintEl.innerHTML = l10n.assessmentMatching.hint;
                this.setHeaderText(popupElement, this.stage);
                break;
            case "6":
                const sequenceClass = new AssessmentSequencePublic();
                assessmentPopupHtml = sequenceClass.view(response);
                popup.open("take-assessment-popup");
                if (questionEl) questionEl.innerHTML = response.question;
                if (bodyEl) bodyEl.innerHTML = assessmentPopupHtml;
                sequenceClass.drag();
                this.buttonsAdding(actionsEl, this.stage);
                popupElement.classList.remove("is-incorrect", "is-correct");
                if (resultEl) resultEl.innerHTML = "";
                if (assessTypeEl) assessTypeEl.innerHTML = l10n.assessmentSequence.header;
                if (assessHintEl) assessHintEl.innerHTML = l10n.assessmentSequence.hint;
                this.setHeaderText(popupElement, this.stage);
                break;
            default:
                return;
        }

        if (assessment) {
            assessment.init(response);
        }
    }

    setHeaderText(popupElement, stage) {
        const popupTitle = this.getFirstAvailable(popupElement, [".cg-popup__title"]);
        const popupSubtitle = this.getFirstAvailable(popupElement, [".cg-popup__subtitle"]);
        const { postAssessment, preAssessment, preAssessmentSubtitle, postAssessmentSubtitle } = l10n.textTopicResultNotification;

        if (popupTitle) {
            popupTitle.textContent = stage === "post" ? postAssessment : preAssessment;
        }
        if (popupSubtitle) {
            popupSubtitle.textContent = stage === "post" ? postAssessmentSubtitle : preAssessmentSubtitle;
        }
    }

    buttonsAdding(buttonSet, stage) {
        if (!buttonSet) {
            return;
        }
        const { iDontKnow } = l10n.customCourse;
        const { submitAnswer, takeLesson, tryAgain, close } = l10n.textTopicResultNotification;

        if (stage === "pre") {
            buttonSet.innerHTML = `
                <button class="btn btn-gray cg-popup__btn assessment-idk">${iDontKnow}</button>
                <button class="btn btn-orange cg-popup__btn assessment-send" type="submit">${submitAnswer}</button>
            `;
        } else {
            buttonSet.innerHTML = `
                <button class="btn btn-orange cg-popup__btn assessment-send" type="submit">${submitAnswer}</button>
                <button class="btn btn-orange cg-popup__btn assessment-take-lesson is-hidden" type="button">${takeLesson}</button>
                <button class="btn btn-orange cg-popup__btn try-again-post is-hidden" type="button">${tryAgain}</button>
                <button class="btn btn-orange cg-popup__btn popup-close is-hidden" type="button">${close}</button>
            `;
        }
    }

    /**
     * Handle submission and checking of the assessment.
     */
    checkResult() {
        const popup = document.querySelector("#take-assessment-popup");
        if (!popup) {
            return;
        }

        // Refresh stage/topic from popup dataset
        this.topicId = popup.dataset.topicId;
        this.assessmentType = popup.dataset.assessmentId;
        this.stage = popup.dataset.assessmentType;

        const data = {
            topic_id: this.topicId,
            assessment_id: this.assessmentType,
            parent_assess_id: this.parentAssessmentId
        };

        switch (this.assessmentType) {
            case "3":
                const multipleChoiceInit = new AssessmentMultipleChoicePublic();
                if (!multipleChoiceInit.checkError(popup)) return;
                data.answers = multipleChoiceInit.addAnswersList(popup);
                break;
            case "4":
                const multipleResponseInit = new AssessmentMultipleResponsePublic();
                if (!multipleResponseInit.checkError(popup)) return;
                data.answers = multipleResponseInit.addAnswersList(popup);
                break;
            case "5":
                const matchingInit = new AssessmentMatchingPublic();
                if (!matchingInit.checkError(popup)) return;
                data.answers = matchingInit.addAnswersList(popup);
                break;
            case "6":
                const sequenceInit = new AssessmentSequencePublic();
                if (!sequenceInit.checkError(popup)) return;
                data.answers = sequenceInit.addAnswersList(popup);
                break;
            default:
                return;
        }

        if (typeof checkAssessmentResultURL === "undefined") {
            console.error("Missing checkAssessmentResultURL");
            return;
        }

        fetchRequestSend(checkAssessmentResultURL, "POST", data).then((response) => {
            this.showResult(response, popup);
            if (response) {
                popup.querySelectorAll("input").forEach((input) => {
                    input.disabled = true;
                });
            }
        });
    }

    /**
     * Display result and update cookie-based statuses.
     */
    showResult(result, popup) {
        // Show result depending on assessment type
        switch (this.assessmentType) {
            case "3":
                new AssessmentMultipleChoicePublic().showResult(result);
                break;
            case "4":
                new AssessmentMultipleResponsePublic().showResult(result);
                break;
            case "5":
                new AssessmentMatchingPublic().showResult(result);
                break;
            case "6":
                new AssessmentSequencePublic().showResult(result);
                break;
            default:
                return;
        }

        // Update status cookies/UI
        if (this.stage === "pre") {
            if (result.status) {
                CGSkillsStatusUpdate.updatePassed(this.stage, this.topicId);
            } else {
                CGSkillsStatusUpdate.updateFailed(this.stage, this.topicId);
            }
        } else {
            this.updatePostStatus(result.status ? "pass" : "fail");
        }

        this.buttonsAfterCheckResult(result, popup);
    }

    /**
     * Update action buttons after the result is shown.
     */
    buttonsAfterCheckResult(result, popup) {
        const { takeLesson, tryAgain, close } = l10n.textTopicResultNotification;
        const { iDontKnow } = l10n.customCourse;
        const actions = this.getFirstAvailable(popup, [".cg-popup__actions", ".buttons-set"]);

        if (!actions) {
            return;
        }

        if (this.stage === "pre") {
            actions.innerHTML = `
                <button class="btn btn-gray cg-popup__btn assessment-idk">${iDontKnow}</button>
                <button class="btn btn-orange cg-popup__btn assessment-take-lesson" type="button">${takeLesson}</button>
            `;
        } else {
            if (result.status) {
                actions.innerHTML = `<button class="btn btn-orange cg-popup__btn popup-close" type="button">${close}</button>`;
            } else {
                actions.innerHTML = `
                    <button class="btn btn-orange cg-popup__btn try-again-post" type="button">${tryAgain}</button>
                    <button class="btn btn-orange cg-popup__btn assessment-take-lesson" type="button">${takeLesson}</button>
                `;
            }
        }
    }

    /**
     * "I don't know" handler: mark skipped and open the tutorial.
     */
    iDontKnowHandle() {
        CGSkillsStatusUpdate.updateSkipped(this.stage, this.topicId);
        this.openTutorialFromPopup();
        new PopupClass().close("take-assessment-popup");
    }

    /**
     * Allow retry for post-assessment.
     */
    tryAgainHandle() {
        const popup = new PopupClass();
        popup.close("take-assessment-popup");
        setTimeout(() => {
            this.run({
                topicId: this.topicId,
                assessmentType: this.assessmentType,
                parentAssessId: this.parentAssessmentId,
                stage: this.stage
            });
        }, 300);
    }

    /**
     * Open tutorial (lesson) for the current topic.
     */
    openTutorialFromPopup() {
        if (!this.topicId) {
            return;
        }
        const row = document.querySelector(`.tr-course-progress[data-row-topic-id="${this.topicId}"]`);
        if (!row) {
            return;
        }
        const tutorialLink = row.querySelector(".td-tutorial a.js-video-popup") || row.querySelector(".td-tutorial a.js-editor-tutorial");
        CGSkillsStatusUpdate.updateUncompleted("tutorial", this.topicId);
        if (tutorialLink) {
            tutorialLink.click();
        }
    }

    /**
     * Update post-assessment status for public (cookie + DOM).
     */
    updatePostStatus(statusValue) {
        const row = document.querySelector(`.course-topic-row[data-id="${this.topicId}"]`);
        if (!row) return;
        const tdPost = row.querySelector(".td-post");
        if (!tdPost) return;

        // Persist status in cookie for refresh resilience.
        setCookie(`post_${this.topicId}`, statusValue, 7);

        // Build a clickable post link for retakes.
        const assessmentType = row.dataset.assessmentType || row.dataset.rowAssessmentType || "";
        const parentAssessId = row.dataset.parentAssessId || row.dataset.rowParentAssessment || "";
        const statusLabelMap = {
            pass: l10n.textTopicResultNotification.pass,
            fail: l10n.textTopicResultNotification.fail,
            skip: l10n.textTopicResultNotification.skip
        };
        const bulletClassMap = {
            pass: "__green",
            fail: "__red",
            skip: "__yellow"
        };

        // Clean existing node
        if (tdPost.querySelector(".course-status")) {
            tdPost.querySelector(".course-status").remove();
        }

        const link = document.createElement("a");
        link.classList.add("course-status", "js-public-assessment");
        link.href = "#";
        link.dataset.stage = "post";
        link.dataset.assessmentType = assessmentType;
        link.dataset.topicId = this.topicId;
        if (parentAssessId) {
            link.dataset.parentAssessId = parentAssessId;
        }
        link.title = l10n.textTopicResultNotification.postAssessment;
        link.innerHTML = `<i class="bullet ${bulletClassMap[statusValue] || "__gray"}"></i><span>${statusLabelMap[statusValue] || l10n.textTopicResultNotification.notStarted}</span>`;

        tdPost.appendChild(link);
        row.classList.add("__light-green-bg");
    }

    /**
     * Return first matching element from provided selectors.
     */
    getFirstAvailable(root, selectors = []) {
        for (const selector of selectors) {
            const el = root.querySelector(selector);
            if (el) return el;
        }
        return null;
    }
}
